import { useSessionStore } from '~/stores/Session';
import {
  CustomRoleBankAccountLevelOverrides,
  CustomRoleEntityLevelOverrides,
  CustomRoleGranularPermissionLevel,
  CustomRolePermissionLevel,
} from '~/typings/API';
import { convertValues } from '~/util';

import { client } from './client';

export interface APIKeyPlatformLevelPermissions {
  achCredits: CustomRolePermissionLevel;
  achDebits: CustomRolePermissionLevel;
  achReturns: CustomRolePermissionLevel;
  bankAccounts: CustomRoleGranularPermissionLevel;
  bookTransfers: CustomRolePermissionLevel;
  counterparties: CustomRolePermissionLevel;
  depositChecks: CustomRolePermissionLevel;
  entities: CustomRolePermissionLevel;
  issueChecks: CustomRolePermissionLevel;
  loans: CustomRolePermissionLevel;
  realtimeTransfers: CustomRolePermissionLevel;
  reporting: CustomRolePermissionLevel;
  stopPayments: CustomRolePermissionLevel;
  wireTransfers: CustomRolePermissionLevel;
}

export interface APIKeyPermissions {
  bankAccountLevelOverrides?: CustomRoleBankAccountLevelOverrides[];
  entityLevelOverrides?: CustomRoleEntityLevelOverrides[];
  platformLevelPermissions: APIKeyPlatformLevelPermissions;
}

export interface CreateAPIKeyRequest {
  platformId: string;
  name: string;
  description?: string;
  permissions?: APIKeyPermissions | null;
}

export interface ListAPIKeysRequest {
  platformId: string;
}

export interface APIKey {
  id: string;
  apiKeyToken: string;
  createdAt?: Date;
  createdBy: string;
  lastUsed?: Date;
  platformId: string;
  name: string;
  description: string;
  permissions?: APIKeyPermissions | null;
}

export interface ListAPIKeysResponse {
  apiKeys: APIKey[];
}

export interface GetAPIKeyRequest {
  platformId: string;
  apiKeyId: string;
}

export interface UpdateAPIKeyRequest {
  name?: string;
  description?: string;
  permissions?: APIKeyPermissions | null;
}

export interface DeleteAPIKeyRequest {
  platformId: string;
  apiKeyId: string;
}

export class ApiKeyRepository {
  static async create(
    platformId: string,
    name: string,
    description: string,
    sandbox?: boolean,
    permissions?: APIKeyPermissions | null
  ) {
    const request: CreateAPIKeyRequest = {
      platformId,
      name,
      description,
    };
    if (permissions !== null) {
      request.permissions = permissions;
    }

    return client
      .post<CreateAPIKeyRequest, APIKey>('/dashboard/api-keys', request, { allowNumbersInRequest: true, sandbox })
      .then((response) => convertValues<APIKey>(response));
  }

  static async get(platformId: string, apiKeyId: string, sandbox?: boolean) {
    return client
      .get<GetAPIKeyRequest, APIKey>(`/dashboard/platforms/${platformId}/api-keys/${apiKeyId}`, { sandbox })
      .then((response) => convertValues<APIKey>(response));
  }

  static async update(
    platformId: string,
    apiKeyId: string,
    name: string,
    description: string,
    sandbox?: boolean,
    permissions?: APIKeyPermissions | null
  ) {
    const request: UpdateAPIKeyRequest = {
      name,
      description,
      permissions,
    };

    return client
      .patch<UpdateAPIKeyRequest, APIKey>(`/dashboard/platforms/${platformId}/api-keys/${apiKeyId}`, request, {
        allowNumbersInRequest: true,
        sandbox,
      })
      .then((response) => convertValues<APIKey>(response));
  }

  static async delete(platformId: string, apiKeyId: string, sandbox?: boolean) {
    return client.delete<DeleteAPIKeyRequest, object>(`/dashboard/platforms/${platformId}/api-keys/${apiKeyId}`, {
      sandbox,
    });
  }

  static async getAll(platformId: string, sandbox?: boolean) {
    return client
      .get<ListAPIKeysRequest, ListAPIKeysResponse>(`/dashboard/platforms/${platformId}/api-keys`, { sandbox })
      .then((response) => ({
        apiKeys: Array.isArray(response?.apiKeys) ? response.apiKeys.map((e) => convertValues<APIKey>(e)) : [],
      }));
  }

  static async getAllList(platformId: string) {
    if (useSessionStore.getState().currentPlatform?.isLiveEnabled) {
      const sandbox = await ApiKeyRepository.getAll(platformId, true);
      const production = await ApiKeyRepository.getAll(platformId, false);

      return {
        apiKeys: [
          ...sandbox.apiKeys.map((e) => ({ ...e, type: 'Sandbox' })),
          ...production.apiKeys.map((e) => ({ ...e, type: 'Production' })),
        ],
      };
    }

    const sandboxSec = await ApiKeyRepository.getAll(platformId, true);

    return {
      apiKeys: [...sandboxSec.apiKeys.map((e) => ({ ...e, type: 'Sandbox' }))],
    };
  }
}
